import { FAUCET } from '@/constant';
import { NavConfig } from '@/routes';
import application from '@/stores/application';
import { TYPE } from '@/theme';
import { Sort as SortIcon } from '@mui/icons-material';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import {
    Box,
    Drawer,
    IconButton,
    Button as MButton,
    Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Button from '../Button';

interface GlobalDrawerProps {
    onMyLazbubuClick?: () => void;
    onBlindBoxClick?: () => void;
    onGiftClick?: () => void;
}

const DrawerContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: calc(100vh - 80px);

    ${({ theme }) => theme.mediaWidth.upToSmall`
    height: calc(100vh - 160px);
    `};
    width: 100%;
    flex-direction: column;
    padding: 1rem;
`;

const DrawerTop = styled.div`
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: flex-start;
    height: 100%;
    width: 100%;
`;

const StyledIconButton = styled(IconButton)`
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 50;
    background-color: white;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
        0 4px 6px -2px rgba(0, 0, 0, 0.05);
    border-radius: 8px;
    transition: background-color 0.15s ease-in-out;

    ${({ theme }) => theme.mediaWidth.upToSmall`
    display: none;
    `};

    &:hover {
        background-color: #f9fafb;
    }
`;

const StyledDrawer = styled(Drawer)`
    & .MuiDrawer-paper {
        width: 240px;
        background-color: white;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
            0 4px 6px -2px rgba(0, 0, 0, 0.05);
        border: none;
        position: fixed;
        top: 80px;
        left: 0;
        height: 100%;
        z-index: 40;
        transform: none;
        transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);

        ${({ theme }) => theme.mediaWidth.upToSmall`
            top: 60px;
        `}
    }

    & .MuiBackdrop-root {
        display: none;
    }
`;

const HeaderBox = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 0;
    min-height: 64px;
    width: 100%;
`;

const StyledTypography = styled(Typography)`
    font-weight: 600;
    color: black;
    font-size: 16px;
`;

const NavButton = styled(MButton)<{ active: boolean }>`
    justify-content: flex-start;
    padding: 24px;
    width: 100%;
    border-radius: 12px;
    text-transform: none;
    font-size: 16px;
    font-weight: 500;
    transition: all 0.15s ease-in-out;
    background-color: ${(props) => (props.active ? '#eff6ff' : 'transparent')};
    color: ${(props) => (props.active ? '#3563F3' : 'black')};

    &:hover {
        background-color: ${(props) => (props.active ? '#dbeafe' : '#f9fafb')};
    }

    & .MuiButton-startIcon {
        color: ${(props) => (props.active ? '#3563F3' : 'black')};
    }
`;

const DrawerBottom = styled.div`
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: flex-start;
    gap: 16px;

    img {
        width: 100%;
    }
`;

const StyledButton = styled(Button)`
    width: 100% !important;
    max-width: unset;
`;

export const GlobalDrawer: React.FC<GlobalDrawerProps> = observer(
    ({ onMyLazbubuClick, onBlindBoxClick, onGiftClick }) => {
        const history = useHistory();
        const location = useLocation();

        const handleDrawerOpen = () => {
            application.openExploreNavDrawer();
        };

        const handleDrawerClose = () => {
            application.closeExploreNavDrawer();
        };

        const handleNavClick = (path: string) => {
            history.push(path);
            // 移动端点击导航后自动关闭抽屉
            if (window.innerWidth < 768) {
                application.closeExploreNavDrawer();
            }
        };

        const isActive = (path: string) => {
            return location.pathname === path;
        };

        // Check if mobile device
        const isMobile = window.innerWidth < 768;
        // Hide button on mobile if incubator drawer is open

        if (location.pathname.indexOf('/agent/') !== -1) {
            return null;
        }

        return (
            <>
                {!application.isExploreNavDrawerOpen && (
                    <StyledIconButton
                        onClick={handleDrawerOpen}
                        aria-label="open drawer"
                    >
                        <SortIcon />
                    </StyledIconButton>
                )}
                <StyledDrawer
                    anchor="left"
                    open={application.isExploreNavDrawerOpen}
                    onClose={handleDrawerClose}
                    variant="persistent"
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                >
                    <DrawerContent>
                        <DrawerTop>
                            <HeaderBox>
                                <StyledTypography>Lazbubu</StyledTypography>
                                <IconButton onClick={handleDrawerClose}>
                                    <KeyboardDoubleArrowLeftIcon />
                                </IconButton>
                            </HeaderBox>

                            {NavConfig.map((item) => {
                                const IconComponent = item.icon;
                                const active = isActive(item.path);

                                return (
                                    <NavButton
                                        key={item.label}
                                        active={active}
                                        onClick={() =>
                                            handleNavClick(item.path)
                                        }
                                        startIcon={<IconComponent />}
                                    >
                                        {item.label}
                                    </NavButton>
                                );
                            })}
                        </DrawerTop>
                        <DrawerBottom>
                            <img
                                src={require('@/assets/images/faucet.png')}
                                alt=""
                            />
                            <TYPE.main fontSize={14}>
                                Get Testnet Tokens
                            </TYPE.main>
                            <StyledButton
                                type="outline"
                                size="small"
                                onClick={() => {
                                    window.open(FAUCET);
                                }}
                            >
                                Claim
                            </StyledButton>
                        </DrawerBottom>
                    </DrawerContent>
                </StyledDrawer>
            </>
        );
    },
);

export default GlobalDrawer;
